import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MapPin, Phone, Clock, Mail, Facebook, Instagram, ExternalLink } from "lucide-react";

const openingHours = [
  { day: "Monday", hours: "8am – 12pm" },
  { day: "Tuesday", hours: "8am – 12pm" },
  { day: "Wednesday", hours: "8am – 12pm" },
  { day: "Thursday", hours: "8am – 12pm" },
  { day: "Friday", hours: "8am – 12pm" },
  { day: "Saturday", hours: "8am – 12pm" },
  { day: "Sunday", hours: "8am – 12pm" },
];

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));

    toast.success("Message sent!", {
      description: "Thanks for getting in touch. We'll get back to you soon."
    });

    setFormData({ name: "", email: "", message: "" });
    setIsSubmitting(false);
  };

  return (
    <div className="page-transition">
      {/* Hero Section */}
      <section className="bg-gradient-hero py-16 lg:py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="font-display text-4xl lg:text-5xl font-bold text-primary-foreground mb-4">
            Contact Us
          </h1>
          <p className="text-primary-foreground/80 text-lg max-w-xl mx-auto">
            Got a question? Want to place a big order? Just fancy a chat? We're here.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              {/* Address Card */}
              <Card className="bg-card border-border shadow-card animate-fade-in">
                <CardHeader>
                  <CardTitle className="font-display text-2xl text-foreground flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <MapPin className="h-5 w-5 text-accent" />
                    </div>
                    Find Us
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-foreground font-medium">Bacon & Brews</p>
                    <p className="text-muted-foreground">
                      59 Torquay Road<br />
                      Paignton<br />
                      Devon<br />
                      TQ3 3DT
                    </p>
                  </div>
                  <a 
                    href="https://maps.google.com/?q=59+Torquay+Road+Paignton+Devon+TQ3+3DT"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-accent hover:text-accent/80 transition-colors font-medium"
                  >
                    Get Directions
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </CardContent>
              </Card>

              {/* Contact Methods */}
              <Card className="bg-card border-border shadow-card animate-fade-in" style={{ animationDelay: '0.1s' }}>
                <CardHeader>
                  <CardTitle className="font-display text-2xl text-foreground flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-wheat/20 flex items-center justify-center">
                      <Phone className="h-5 w-5 text-wheat" />
                    </div>
                    Get in Touch
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <a 
                    href="tel:+447424864732"
                    className="flex items-center gap-3 text-foreground hover:text-accent transition-colors p-3 rounded-lg hover:bg-secondary"
                  >
                    <Phone className="h-5 w-5 text-muted-foreground" />
                    <span className="font-medium">07424 864732</span>
                  </a>
                  <a 
                    href="mailto:hello@baconandbrews.co.uk"
                    className="flex items-center gap-3 text-foreground hover:text-accent transition-colors p-3 rounded-lg hover:bg-secondary"
                  >
                    <Mail className="h-5 w-5 text-muted-foreground" />
                    <span className="font-medium">hello@baconandbrews.co.uk</span>
                  </a>
                </CardContent>
              </Card>

              {/* Social & Google Business */}
              <Card className="bg-card border-border shadow-card animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <CardHeader>
                  <CardTitle className="font-display text-2xl text-foreground">
                    Follow Us
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4 mb-6">
                    <a 
                      href="#" 
                      className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center hover:bg-accent hover:text-accent-foreground transition-colors"
                      aria-label="Facebook"
                    >
                      <Facebook className="h-5 w-5" />
                    </a>
                    <a 
                      href="#" 
                      className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center hover:bg-accent hover:text-accent-foreground transition-colors"
                      aria-label="Instagram"
                    >
                      <Instagram className="h-5 w-5" />
                    </a>
                  </div>
                  <a 
                    href="#"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-accent hover:text-accent/80 transition-colors font-medium"
                  >
                    View on Google Business
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </CardContent>
              </Card>

              {/* Opening Hours */}
              <Card className="bg-card border-border shadow-card animate-fade-in" style={{ animationDelay: '0.3s' }}>
                <CardHeader>
                  <CardTitle className="font-display text-2xl text-foreground flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-tea/20 flex items-center justify-center">
                      <Clock className="h-5 w-5 text-tea" />
                    </div>
                    Opening Hours
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {openingHours.map((item) => (
                      <div key={item.day} className="flex justify-between py-2 border-b border-border last:border-0">
                        <span className="text-foreground">{item.day}</span>
                        <span className="text-muted-foreground font-medium">{item.hours}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="animate-slide-in-right">
              <Card className="bg-card border-border shadow-elevated sticky top-24">
                <CardHeader>
                  <CardTitle className="font-display text-2xl text-foreground">
                    Send Us a Message
                  </CardTitle>
                  <p className="text-muted-foreground">
                    Questions, feedback, or bulk orders? Drop us a line.
                  </p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-foreground">Name</Label>
                      <Input
                        id="name"
                        name="name"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="bg-background"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-foreground">Email</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="bg-background"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-foreground">Message</Label>
                      <Textarea
                        id="message"
                        name="message"
                        placeholder="What can we help you with?"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="bg-background resize-none"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      variant="hero" 
                      size="lg" 
                      className="w-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Sending..." : "Send Message"}
                    </Button>
                  </form>

                  <p className="text-xs text-muted-foreground mt-4 text-center">
                    We'll get back to you as soon as we can.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="bg-secondary">
        <div className="container mx-auto px-4 py-8">
          <div className="aspect-[21/9] rounded-xl overflow-hidden shadow-card bg-muted flex items-center justify-center">
            <div className="text-center p-8">
              <MapPin className="h-12 w-12 text-accent mx-auto mb-4" />
              <p className="text-foreground font-medium text-lg mb-2">
                59 Torquay Road, Paignton, Devon TQ3 3DT
              </p>
              <a 
                href="https://maps.google.com/?q=59+Torquay+Road+Paignton+Devon+TQ3+3DT"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:text-accent/80 transition-colors inline-flex items-center gap-2"
              >
                Open in Google Maps
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-20 bg-gradient-hero">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-primary-foreground mb-4">
            Ready to Order?
          </h2>
          <p className="text-primary-foreground/80 text-lg mb-8 max-w-xl mx-auto">
            Skip the queue – order ahead and we'll have it ready when you arrive.
          </p>
          <Button asChild variant="wheat" size="xl">
            <Link to="/order">Order Now</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
